import './Checkout.css'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../context/CartContext'
import { useUser } from '../context/UserContext'
import { DELIVERY } from '../data/site'
import { payWithRazorpay } from '../services/razorpay.js'

const inr = n => `₹${n.toLocaleString('en-IN')}`

function quote(method, pin, subtotal) {
  if (method === 'pickup') return { ok: true, fee: 0, eta: 'Ready for pickup within 2 hours', label: 'Store pickup' }
  if (!/^\d{6}$/.test(pin)) return { ok: false, msg: 'Enter a 6-digit pincode to see delivery cost.' }
  const zone = DELIVERY.localPrefixes.some(p => pin.startsWith(p)) ? DELIVERY.local : DELIVERY.india
  return { ok: true, fee: subtotal >= zone.freeAbove ? 0 : zone.fee, eta: zone.eta, label: zone.label }
}

// every field is required; address, city and pincode only for home delivery
function validate(f, method) {
  const e = {}
  const name = f.name.trim()
  if (!name) e.name = 'Enter your full name.'
  else if (name.length < 2 || !/^\p{L}[\p{L} .'-]*$/u.test(name)) e.name = 'Enter a valid name (letters only).'

  if (!f.phone) e.phone = 'Enter your phone number.'
  else if (!/^[6-9]\d{9}$/.test(f.phone)) e.phone = 'Enter a valid 10-digit mobile number.'

  const email = f.email.trim()
  if (!email) e.email = 'Enter your email address.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) e.email = 'Enter a valid email, like name@example.com.'

  if (method === 'home') {
    if (!f.address.trim()) e.address = 'Enter your delivery address.'
    else if (f.address.trim().length < 10) e.address = 'Enter your full address (at least 10 characters).'

    const city = f.city.trim()
    if (!city) e.city = 'Enter your city.'
    else if (!/^\p{L}[\p{L} .'-]*$/u.test(city)) e.city = 'City can only have letters.'

    if (!f.pin) e.pin = 'Enter your pincode.'
    else if (!/^\d{6}$/.test(f.pin)) e.pin = 'Pincode must be 6 digits.'
  }
  return e
}

export default function Checkout() {
  const { items, total, checkout, setCheckout, dispatch } = useCart()
  const { user, openAuth } = useUser()
  const navigate = useNavigate()
  const [f, setF] = useState({ name: '', phone: '', email: '', address: '', city: '', pin: '' })
  const [method, setMethod] = useState('home')
  const [status, setStatus] = useState('form') // form | paying | done
  const [error, setError] = useState('')
  const [paymentId, setPaymentId] = useState('')
  const [eta, setEta] = useState('')
  const [orderNo, setOrderNo] = useState('')
  const [confirmed, setConfirmed] = useState(true)
  const [touched, setTouched] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const set = (k, v) => setF(x => ({ ...x, [k]: v }))
  // every time checkout opens, fill name, phone and email from the signed-in account
  useEffect(() => {
    if (!checkout || !user) return
    setF(x => ({
      ...x,
      name: user.name || '',
      phone: String(user.phone || '').replace(/\D/g, '').slice(-10),
      email: user.email || '',
    }))
  }, [checkout, user])
  // checkout needs an account: open "Create your account" first
  useEffect(() => { if (checkout && !user) openAuth('signup') }, [checkout, user, openAuth])
  if (!checkout) return null

  const errors = validate(f, method)
  const show = k => (touched[k] || submitted) && errors[k]
  const blur = k => () => setTouched(t => ({ ...t, [k]: true }))
  // props shared by every input: id, error state and accessibility links
  const field = k => ({
    id: `co-${k}`,
    'aria-invalid': !!show(k),
    'aria-describedby': show(k) ? `co-${k}-err` : undefined,
    onBlur: blur(k),
  })
  const msg = k => show(k) && <small className="co__err" id={`co-${k}-err`} role="alert">{errors[k]}</small>

  const q = quote(method, f.pin, total)
  const grand = total + (q.ok ? q.fee : 0)
  const close = () => { setCheckout(false); setStatus('form'); setError(''); setSubmitted(false); setTouched({}) }

  const pay = e => {
    e.preventDefault()
    setSubmitted(true)
    const bad = Object.keys(errors)
    if (bad.length) {
      setError('Please fix the highlighted fields.')
      document.getElementById(`co-${bad[0]}`)?.focus()
      return
    }
    if (!q.ok) return setError(q.msg)
    const customer = { ...f, name: f.name.trim(), email: f.email.trim(), address: f.address.trim(), city: f.city.trim() }
    setError(''); setStatus('paying')
    payWithRazorpay({
      checkout: {
        items: items.map(i => ({ id: i.id, name: i.name, qty: i.qty, price: i.price })),
        delivery_fee: q.ok ? q.fee : 0,
        delivery_method: method, // 'home' | 'pickup'
        name: customer.name,
        phone: customer.phone,
        email: customer.email,
        address: method === 'home' ? customer.address : '',
        city: method === 'home' ? customer.city : '',
        pincode: method === 'home' ? customer.pin : '',
      },
      notes: { delivery: method, address: `${customer.address}, ${customer.city} ${customer.pin}`, items: items.map(i => `${i.qty}x ${i.name}`).join('; ') },
      // r = { paymentId, orderNo, confirmed }. The server saved the order before the popup opened and marks it paid in verify.
      onSuccess: r => {
        setPaymentId(r.paymentId); setOrderNo(r.orderNo); setConfirmed(r.confirmed)
        setEta(q.eta); dispatch({ type: 'clear' }); setStatus('done')
      },
      onFailure: m => { setError(m); setStatus('form') },
    })
  }

  return (
    <div className="co" role="dialog" aria-modal="true" aria-label="Checkout">
      <div className="co__scrim" onClick={close} />
      <div className="co__box">
        {!user ? (
          <div className="co__done">
            <h2>Create an account to check out</h2>
            <p>It takes a minute. We use your name, phone and email to fill in checkout for you. Your cart is saved.</p>
            <button className="btn btn--cobalt" onClick={() => openAuth('signup')}>Create account</button>
            <button className="co__link" onClick={() => openAuth('login')}>I already have an account</button>
          </div>
        ) : status === 'done' ? (
          <div className="co__done">
            <h2>Payment received</h2>
            <p>Thank you, {f.name.trim()}. Your order is confirmed.</p>
            {orderNo && <p><strong>Order number:</strong> {orderNo}</p>}
            <p><strong>Payment ID:</strong> {paymentId}</p>
            {!confirmed && <p className="co__error">Your payment went through, but we could not confirm the order on our side yet. Please keep this Payment ID and contact us. We will sort it out.</p>}
            <p><strong>Delivery:</strong> {eta}</p>
            <button className="btn btn--cobalt" onClick={() => { close(); navigate('/orders', { state: { orderNo } }) }}>View my order</button>
            <button className="co__link" onClick={close}>Continue shopping</button>
          </div>
        ) : (
          <>
            <form className="co__form" onSubmit={pay} noValidate>
              <h2>Checkout</h2>
              <p className="co__note">Your name, phone and email are filled in from your account. You can edit them for this order.</p>
              <div className="co__methods" role="radiogroup" aria-label="Delivery method">
                <button type="button" className={`chip ${method === 'home' ? 'chip--on' : ''}`} aria-pressed={method === 'home'} onClick={() => setMethod('home')}>Home delivery</button>
                <button type="button" className={`chip ${method === 'pickup' ? 'chip--on' : ''}`} aria-pressed={method === 'pickup'} onClick={() => setMethod('pickup')}>Pick up at store</button>
              </div>
              <label>Full name
                <input {...field('name')} autoComplete="name" value={f.name} onChange={e => set('name', e.target.value)} required />
                {msg('name')}
              </label>
              <div className="co__two">
                <label>Phone
                  <input {...field('phone')} type="tel" inputMode="numeric" autoComplete="tel-national" maxLength="10" placeholder="10-digit mobile number"
                    value={f.phone} onChange={e => set('phone', e.target.value.replace(/\D/g, ''))} required />
                  {msg('phone')}
                </label>
                <label>Email
                  <input {...field('email')} type="email" autoComplete="email" placeholder="name@example.com" value={f.email} onChange={e => set('email', e.target.value)} required />
                  {msg('email')}
                </label>
              </div>
              {method === 'home' && (
                <>
                  <label>Address
                    <textarea {...field('address')} rows="2" autoComplete="street-address" value={f.address} onChange={e => set('address', e.target.value)} required />
                    {msg('address')}
                  </label>
                  <div className="co__two">
                    <label>City
                      <input {...field('city')} autoComplete="address-level2" value={f.city} onChange={e => set('city', e.target.value)} required />
                      {msg('city')}
                    </label>
                    <label>Pincode
                      <input {...field('pin')} inputMode="numeric" autoComplete="postal-code" maxLength="6" value={f.pin} onChange={e => set('pin', e.target.value.replace(/\D/g, ''))} required />
                      {msg('pin')}
                    </label>
                  </div>
                </>
              )}
              {error && <p className="co__error" role="alert">{error}</p>}
              <button className="btn btn--cobalt" disabled={status === 'paying'}>
                {status === 'paying' ? 'Opening Razorpay…' : `Pay ${inr(grand)} with Razorpay`}
              </button>
            </form>
            <aside className="co__sum">
              <h3>Order summary</h3>
              <ul>{items.map(i => <li key={i.id}><span>{i.qty} × {i.name}</span><span>{inr(i.qty * i.price)}</span></li>)}</ul>
              <p><span>Subtotal</span><span>{inr(total)}</span></p>
              <p><span>Delivery</span><span>{q.ok ? (q.fee ? inr(q.fee) : 'Free') : '-'}</span></p>
              {q.ok && <small>{q.label}: {q.eta}</small>}
              <p className="co__grand"><span>Total</span><span>{inr(grand)}</span></p>
            </aside>
          </>
        )}
        <button className="co__x btn btn--sm" onClick={close} aria-label="Close checkout">Close</button>
      </div>
    </div>
  )
}